import type { CapabilityOption, PolicyRevision, PolicyScope, RelationshipRole, RelationshipRoleOption } from '../hooks/usePermissionsAdministration';

export const ENFORCEMENT_LABELS = {
  policyEnforced: 'Policy enforced',
  separateAccessRules: 'Separate access rules',
  notYetEnforcedHere: 'Not yet enforced here',
} as const;

const SCOPE_LABELS: Record<PolicyScope, string> = {
  self: 'Assigned resource / self',
  subcommittee: 'Responsibility areas',
  district: 'District',
  council: 'Council',
};

export function permissionScopeLabel(capability: CapabilityOption, scope: PolicyScope): string {
  if (capability.domain === 'actions' && scope === 'self') return 'Assigned';
  return SCOPE_LABELS[scope];
}

export function policyIntentNotice(capability: CapabilityOption): string {
  if (capability.enforcement.status === 'policyEnforced') return '';
  if (capability.enforcement.status === 'notYetEnforcedHere') {
    return 'Policy intent only: this change does not currently alter live access.';
  }
  return 'Policy intent only: live access for this operation is currently controlled by separate access rules.';
}

type RolePolicy = PolicyRevision['policy'];

/** The capability keys a policy gives one relationship role. */
export function roleGrantKeys(policy: RolePolicy, role: RelationshipRole): string[] {
  return (policy.relationshipRoleGrants ?? []).find((entry) => entry.role === role)?.grants.map((grant) => grant.capabilityKey) ?? [];
}

/**
 * Returns a copy of the policy with one relationship-role grant switched on or off. Switching a grant on also switches on the
 * role-grantable capabilities it depends on, and switching one off also switches off whatever depends on it, so the draft
 * the editor builds is never one the server would reject for a missing companion. The server remains the authority.
 */
export function withRoleGrant(
  policy: RolePolicy,
  option: RelationshipRoleOption,
  capabilityKey: string,
  granted: boolean,
): RolePolicy {
  const offered = new Map(option.capabilities.map((capability) => [capability.key, capability]));
  const keys = new Set(roleGrantKeys(policy, option.role));
  if (granted) {
    const add = (key: string) => {
      if (keys.has(key) || !offered.has(key)) return;
      keys.add(key);
      for (const required of offered.get(key)!.requires) add(required);
    };
    add(capabilityKey);
  } else {
    const remove = (key: string) => {
      if (!keys.delete(key)) return;
      for (const capability of option.capabilities) if (capability.requires.includes(key)) remove(capability.key);
    };
    remove(capabilityKey);
  }
  const others = (policy.relationshipRoleGrants ?? []).filter((entry) => entry.role !== option.role);
  const entry = { role: option.role, grants: [...keys].sort().map((key) => ({ capabilityKey: key, state: 'granted' as const })) };
  const relationshipRoleGrants = [...others, ...(entry.grants.length ? [entry] : [])];
  const { relationshipRoleGrants: _previous, ...rest } = policy;
  return relationshipRoleGrants.length ? { ...rest, relationshipRoleGrants } : rest;
}
